import React from "react";
import { Row, Col } from "reactstrap";
import { FiArrowLeft } from "react-icons/fi";
import { FaStar } from "react-icons/fa";

const IMAGE_HOST = "https://api.sandbox.blu-ocean.net/cms-service";

const formatDate = (dateStr) => {
  if (!dateStr) {
    return null;
  }

  const date = new Date(dateStr);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const SummaryRow = ({ label, value, isLast }) => (
  <div
    className="d-flex align-items-center px-4 py-3"
    style={{
      borderBottom: isLast ? "none" : "1px solid #f1f3f5",
    }}
  >
    <div
      className="fw-semibold"
      style={{
        width: 150,
        minWidth: 150,
        color: "#495057",
      }}
    >
      {label}
    </div>

    <div
      style={{
        color: value ? "#212529" : "#adb5bd",
        fontStyle: value ? "normal" : "italic",
      }}
    >
      {value || "Not set"}
    </div>
  </div>
);

function Preview({ data = {}, onEditBasic, onEditReward }) {
  const {
    offertitle,
    description,
    category,
    bannerimage,
    pointsrequired,
    discounttype,
    discountvalue,
    minOrderValue,
    startdate,
    enddate,
  } = data;

  const discountLabel = discountvalue
    ? discounttype === "percentage"
      ? `${discountvalue}% off`
      : `₹${discountvalue} off`
    : null;

  const minOrderLabel =
    minOrderValue !== undefined && minOrderValue !== ""
      ? Number(minOrderValue) === 0
        ? "No minimum"
        : `₹${minOrderValue}`
      : "No minimum";

  const getBannerPreviewUrl = (image) => {
    if (!image) {
      return null;
    }

    // New uploaded File
    if (image instanceof File) {
      return URL.createObjectURL(image);
    }

    // String URL/path
    if (typeof image === "string") {
      if (image.startsWith("http://") || image.startsWith("https://")) {
        return image;
      }

      return `${IMAGE_HOST}${image.startsWith("/") ? "" : "/"}${image}`;
    }

    // Uploaded image object
    if (typeof image === "object") {
      const imagePath =
        image.url ||
        image.fileUrl ||
        image.imageUrl ||
        image.downloadUrl ||
        image.publicUrl ||
        image.location ||
        null;

      if (!imagePath) {
        return null;
      }

      if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
        return imagePath;
      }

      return `${IMAGE_HOST}${imagePath.startsWith("/") ? "" : "/"}${imagePath}`;
    }

    return null;
  };

  const bannerPreviewUrl = getBannerPreviewUrl(bannerimage);

  const startLabel = formatDate(startdate);
  const endLabel = formatDate(enddate);

  return (
    <div>
      <Row className="g-4">
        {/* SUMMARY */}
        <Col xs={12} lg={7}>
          <div className="card border-0 shadow-sm rounded-4">
            <div className="p-4 pb-3 border-bottom">
              <h4 className="fw-bold mb-0">Offer Summary</h4>
            </div>

            <SummaryRow label="Title" value={offertitle} />

            <SummaryRow label="Category" value={category} />

            <SummaryRow label="Description" value={description} />

            <SummaryRow
              label="Points Required"
              value={pointsrequired ? `${pointsrequired} pts` : null}
            />

            <SummaryRow label="Discount" value={discountLabel} />

            <SummaryRow label="Min. Order" value={minOrderLabel} />

            <SummaryRow label="Valid From" value={startLabel} />

            <SummaryRow label="Valid Until" value={endLabel} />

            <SummaryRow
              label="Banner Image"
              value={
                bannerimage
                  ? bannerimage.name || "Banner image selected"
                  : "None"
              }
              isLast
            />
          </div>

          {/* EDIT BUTTONS */}
          <div className="d-flex flex-wrap gap-2 mt-4">
            <button
              type="button"
              className="btn rounded-3 fw-semibold"
              style={{
                border: "1px solid #dee2e6",
                color: "#212529",
              }}
              onClick={onEditBasic}
            >
              <FiArrowLeft className="me-2" />
              Edit Basic Details
            </button>

            <button
              type="button"
              className="btn rounded-3 fw-semibold"
              style={{
                border: "1px solid #dee2e6",
                color: "#212529",
              }}
              onClick={onEditReward}
            >
              <FiArrowLeft className="me-2" />
              Edit Reward Details
            </button>
          </div>
        </Col>

        {/* APP PREVIEW */}
        <Col xs={12} lg={5}>
          <div className="d-flex flex-column align-items-center h-100">
            <div
              className="text-uppercase fw-semibold mb-3"
              style={{
                letterSpacing: 1,
                color: "#868e96",
                fontSize: 13,
              }}
            >
              App Preview
            </div>

            {/* PHONE */}
            <div
              style={{
                width: "100%",
                maxWidth: 300,
                borderRadius: 40,
                background: "#111",
                padding: 14,
                boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
              }}
            >
              {/* NOTCH */}
              <div className="d-flex justify-content-center mb-2">
                <div
                  style={{
                    width: 90,
                    height: 20,
                    background: "#111",
                    borderRadius: 20,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    paddingRight: 8,
                  }}
                >
                  <div
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: "#222",
                    }}
                  />
                </div>
              </div>

              {/* SCREEN */}
              <div
                style={{
                  background: "#f8f9fa",
                  borderRadius: 26,
                  padding: 16,
                  minHeight: 480,
                }}
              >
                <div
                  className="fw-bold mb-2"
                  style={{
                    fontSize: 12,
                    letterSpacing: 0.5,
                  }}
                >
                  AVAILABLE OFFERS
                </div>

                {/* OFFER CARD */}
                <div
                  className="rounded-4 overflow-hidden mb-3"
                  style={{
                    background: "#fff",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                  }}
                >
                  {/* BANNER */}
                  <div
                    className="position-relative d-flex flex-column justify-content-center align-items-center text-white text-center"
                    style={{
                      height: 130,
                      overflow: "hidden",
                      background: bannerPreviewUrl
                        ? "#ddd"
                        : "linear-gradient(135deg, #fb923c, #f97316)",
                    }}
                  >
                    {bannerPreviewUrl ? (
                      <>
                        {/* BLURRED IMAGE */}
                        <img
                          src={bannerPreviewUrl}
                          alt="Offer Banner"
                          style={{
                            position: "absolute",
                            inset: "-8px",
                            width: "calc(100% + 16px)",
                            height: "calc(100% + 16px)",
                            objectFit: "cover",
                            filter: "blur(0.5px)",
                            zIndex: 0,
                          }}
                        />

                        {/* DARK OVERLAY */}
                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                            background: "rgba(0, 0, 0, 0.35)",
                            zIndex: 1,
                          }}
                        />
                      </>
                    ) : null}

                    {/* CONTENT OVER IMAGE */}
                    {discountLabel && (
                      <div
                        className="fw-bold position-relative"
                        style={{
                          fontSize: 18,
                          zIndex: 2,
                          textShadow: "0 1px 3px rgba(0,0,0,0.5)",
                        }}
                      >
                        {discountLabel.replace(" off", "")}
                      </div>
                    )}

                    <div
                      className="mt-1 position-relative"
                      style={{
                        fontSize: 12,
                        opacity: 0.9,
                        zIndex: 2,
                        textShadow: "0 1px 3px rgba(0,0,0,0.5)",
                      }}
                    >
                      on eligible items
                    </div>
                  </div>

                  {/* CONTENT */}
                  <div className="p-3">
                    <div
                      className="fw-bold"
                      style={{
                        fontSize: 14,
                      }}
                    >
                      {offertitle || "Your offer title"}
                    </div>

                    <div
                      className="text-muted mb-2"
                      style={{
                        fontSize: 12,
                      }}
                    >
                      {description || "Offer description will appear here."}
                    </div>

                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div
                        className="d-flex align-items-center gap-1 fw-semibold"
                        style={{
                          fontSize: 12,
                        }}
                      >
                        <FaStar size={12} color="#f97316" />
                        {pointsrequired || "—"} pts
                      </div>

                      <div
                        style={{
                          fontSize: 11,
                          color: "#868e96",
                        }}
                      >
                        {endLabel ? `Until ${endLabel}` : "No end date"}
                      </div>
                    </div>

                    <button
                      type="button"
                      className="btn text-white w-100 fw-semibold rounded-3"
                      style={{
                        background: "var(--rv-primary-color, #f97316)",
                        fontSize: 13,
                        padding: "8px 0",
                      }}
                    >
                      Redeem Offer
                    </button>
                  </div>
                </div>

                {/* SKELETON */}
                <div
                  className="rounded-4 mb-3"
                  style={{
                    height: 70,
                    background: "#e9ecef",
                  }}
                />

                <div
                  className="rounded-4"
                  style={{
                    height: 70,
                    background: "#e9ecef",
                    marginBottom: 20,
                  }}
                />

                {/* HOME INDICATOR */}
                <div
                  className="d-flex justify-content-center"
                  style={{
                    paddingTop: 8,
                  }}
                >
                  <div
                    style={{
                      width: 110,
                      height: 4,
                      borderRadius: 4,
                      background: "#212529",
                      opacity: 0.4,
                    }}
                  />
                </div>
              </div>
            </div>

            <div
              className="text-muted mt-3 text-center"
              style={{
                fontSize: 12,
              }}
            >
              Live preview · updates as you type
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
}

export default Preview;
